import React from 'react'

import { format as formatDate } from 'date-fns'
import { Form, Formik } from 'formik'
import Select from 'react-select'

import {
  setClosedLossDrawerReason,
  setClosedLossDrawerSubReasons,
  setClosedLossDrawerType,
  setEscalationApprovalDrawerEscalation,
  setEscalationApprovalDrawerOpen,
  setUAMDeclineEscalation,
  setUAMDeclineModalOpen,
} from '@/client/generalSlice'
import { useAppDispatch } from '@/client/hooks'
import { MultiSelectField } from '@/components/FormikInputs'
import TextEditorField from '@/components/FormikInputs/TextEditorField'
import { escalationFlagsTypes } from '@/constants/escalations'
import { subLossReasonsOptions } from '@/features/ClosedLossDrawer/options'
import { solarExceptionPathOptions } from '@/features/application/applicationSlice'
import { useUpdateEscalationMutation } from '@/services/escalations'
import { useGetLoggedUserQuery } from '@/services/user'
import { Escalation as EscalationType } from '@/types/unlock'
import { userHasPermissionToApprove } from '@/utils/escalations'
import { isHeoManager, isUAMUser, isUWUser, isUnderwritingUser } from '@/utils/user'

export type EscalationProps = {
  escalation: EscalationType
  documents: any[]
}

export default function Escalation(props: EscalationProps) {
  const { escalation } = props
  const isExceptionTab = escalation.tab === 'exceptions'

  const { data: loggedUser } = useGetLoggedUserQuery()

  const dispatch = useAppDispatch()
  const hasPermission =
    loggedUser?.is_superuser ||
    isUAMUser(loggedUser) ||
    isUWUser(loggedUser) ||
    isUnderwritingUser(loggedUser) ||
    isHeoManager(loggedUser)
  const hasPermissionToApprove = userHasPermissionToApprove(loggedUser, escalation)
  const typeColor =
    escalation.tab === 'exceptions'
      ? escalationFlagsTypes.includes(escalation.type)
        ? 'text-orange-400'
        : 'text-error'
      : ''

  const [updateEscalation] = useUpdateEscalationMutation()

  const setLossReason = () => {
    const escalationTypeLossReasonMapping = {
      'property-atypicality': { lossReason: 'atypical-property' },
      'mortgage-late': { lossReason: 'credit' },
      foreclosure: {
        lossReason: 'credit',
        subLossReason: { value: 'mtg-repo-or-fc', label: 'MTG Repo or FC' },
      },
      'flag-home-value': { lossReason: 'home-value' },
      'home-value': { lossReason: 'home-value' },
      'avm-not-available': { lossReason: 'avm-data' },
    }

    const lossReason =
      escalation?.type in escalationTypeLossReasonMapping
        ? escalationTypeLossReasonMapping[escalation?.type]?.lossReason
        : null

    if (lossReason && 'subLossReason' in escalationTypeLossReasonMapping[escalation?.type]) {
      dispatch(
        setClosedLossDrawerSubReasons([
          escalationTypeLossReasonMapping[escalation?.type]['subLossReason'],
        ])
      )
    }
    if (lossReason) {
      dispatch(setClosedLossDrawerReason(lossReason))
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{
        uuid: escalation.uuid,
        approved: escalation.approved,
        request_comment: escalation.request_comment,
        response_comment: escalation.response_comment,
        tagged_users: escalation.tagged_users || [],
        path_solar_panel: escalation.path_solar_panel,
        uam_commentary: escalation.uam_commentary,
        sub_loss_reasons: escalation?.sub_loss_reasons,
        other_text:
          escalation?.sub_loss_reasons?.length && escalation?.sub_loss_reasons[0]?.other_text
            ? escalation?.sub_loss_reasons[0]?.other_text
            : '',
      }}
      onSubmit={async (values: Partial<EscalationType>) => {
        await updateEscalation(values)
      }}
    >
      {({ values, submitForm, setFieldValue }) => (
        <Form
          className='mt-4'
          data-test='escalation-exception-section'
        >
          <div className='w-full card bg-base-100 shadow-md flex flex-col border'>
            <div className={`flex justify-between px-4 py-2 rounded-t-box bg-[#fabe23]`}>
              <div className='flex flex-row gap-2 items-center'>
                <p className='text-sm card-title'>{escalation.title}</p>
              </div>
            </div>
            <div className='flex flex-col w-full px-4 py-2 gap-3'>
              <div className='flex flex-col gap-2'>
                <p className='font-bold text-sm'>Request</p>
                <div className='text-xs flex flex-col gap-1'>
                  <p data-test='escalation-request-user'>
                    User:{' '}
                    {escalation.request_user?.id
                      ? escalation.request_user?.full_name || '~'
                      : 'Maximus X'}
                  </p>
                  <p data-test='escalation-request-date'>
                    Date:{' '}
                    {escalation.request_date
                      ? formatDate(new Date(escalation.request_date), 'hh:mm aa MM/dd')
                      : escalation.created_at
                        ? formatDate(new Date(escalation.created_at), 'hh:mm aa MM/dd')
                        : '~'}
                  </p>
                  <span>
                    Type:{' '}
                    <span
                      className={`${typeColor} font-bold`}
                      data-test='escalation-request-type'
                    >
                      {escalation.type_label}
                    </span>
                  </span>
                </div>
              </div>
              <div className='text-xs flex flex-col gap-1'>
                <p
                  className='font-bold text-sm'
                  data-test='escalation-response-section'
                >
                  Response
                </p>
                <div className='text-xs flex flex-col gap-1'>
                  <p data-test='escalation-response-user'>
                    User: {escalation.response_user?.full_name || '~'}
                  </p>
                  <p data-test='escalation-response-date'>
                    Date:{' '}
                    {escalation.response_date
                      ? formatDate(new Date(escalation.response_date), 'hh:mm aa MM/dd')
                      : '~'}
                  </p>
                </div>
              </div>
              <fieldset
                disabled={isExceptionTab && !hasPermissionToApprove}
                className='flex items-center justify-between'
              >
                <p className='font-bold text-sm'>Approved:</p>
                <div className='flex flex-row gap-2'>
                  <button
                    type='button'
                    className={`btn btn-xs ${
                      values.approved === true ? 'btn-success disabled:text-green-700' : null
                    }`}
                    onClick={() => {
                      dispatch(setEscalationApprovalDrawerOpen(true))
                      dispatch(setEscalationApprovalDrawerEscalation(escalation))
                    }}
                    data-test='escalation-approved-yes-btn'
                  >
                    YES
                  </button>
                  <button
                    type='button'
                    className={`btn btn-xs ${
                      values.approved === false ? 'btn-error disabled:text-red-700' : null
                    }`}
                    onClick={() => {
                      setLossReason()
                      dispatch(setClosedLossDrawerType('denied'))
                      dispatch(setUAMDeclineEscalation(escalation))
                      dispatch(setUAMDeclineModalOpen(true))
                    }}
                    data-test='escalation-approved-no-btn'
                  >
                    NO
                  </button>
                </div>
              </fieldset>
              {escalation.type == 'roof-solar-panel' && (
                <div>
                  <Select
                    options={solarExceptionPathOptions}
                    name='path_solar_panel'
                    value={solarExceptionPathOptions.find(
                      (option) => option.value === escalation.path_solar_panel
                    )}
                    onChange={(option) => {
                      setFieldValue('path_solar_panel', option?.value)
                      submitForm()
                    }}
                    placeholder='Select Solar Exception Path'
                    isDisabled
                    className={`text-sm`}
                  />
                </div>
              )}
              <div className='flex flex-col gap-1'>
                <p className='font-bold text-sm'>
                  {isExceptionTab ? 'Exception Risk Mitigate Factors' : 'Requestor Commentary'}
                </p>
                {isExceptionTab ? (
                  <TextEditorField
                    onChangeApplied={submitForm}
                    setFieldValue={setFieldValue}
                    name='request_comment'
                    disabled={!hasPermission}
                  />
                ) : (
                  <TextEditorField
                    onChangeApplied={submitForm}
                    setFieldValue={setFieldValue}
                    name='request_comment'
                    allowMentions={true}
                    onMention={(user) => {
                      setFieldValue('tagged_users', [...values.tagged_users, user.id])
                    }}
                  />
                )}
              </div>
              {isExceptionTab && escalation?.response_comment && (
                <div className='flex flex-col gap-1'>
                  <p className='font-bold text-sm'>Commentary</p>
                  <TextEditorField
                    onChangeApplied={submitForm}
                    setFieldValue={setFieldValue}
                    name='response_comment'
                    allowMentions={true}
                    onMention={(user) => {
                      setFieldValue('tagged_users', [...values.tagged_users, user.id])
                    }}
                  />
                </div>
              )}
              {escalation?.approved === false && !!escalation?.sub_loss_reasons?.length && (
                <div className='flex flex-col gap-1'>
                  {escalation?.sub_loss_reasons[0]?.other_text ? (
                    <>
                      <p className='font-bold text-sm'>UAM Decline Reason</p>
                      <TextEditorField
                        name='other_text'
                        setFieldValue={setFieldValue}
                        value={escalation?.sub_loss_reasons[0]?.other_text}
                        disabled
                      />
                    </>
                  ) : (
                    <MultiSelectField
                      name='sub_loss_reasons'
                      placeholder='Select sub loss reasons'
                      multiple
                      options={Object.values(subLossReasonsOptions).flat()}
                      disabled
                    />
                  )}
                </div>
              )}
              {escalation?.uam_commentary && (
                <div className='flex flex-col gap-1'>
                  <p
                    className='font-bold text-sm'
                    data-test='escalation-uam-decline-commentary-label'
                  >
                    UW Decline Commentary
                  </p>

                  <TextEditorField
                    onChangeApplied={submitForm}
                    setFieldValue={setFieldValue}
                    name='uam_commentary'
                    disabled={true}
                  />
                </div>
              )}
              {!isExceptionTab && (
                <div className='flex flex-col gap-1'>
                  <p className='font-bold text-sm'>Responder Commentary</p>
                  <TextEditorField
                    onChangeApplied={submitForm}
                    setFieldValue={setFieldValue}
                    name='response_comment'
                    allowMentions={true}
                    onMention={(user) => {
                      setFieldValue('tagged_users', [...values.tagged_users, user.id])
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  )
}
